"use client";

import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import styles from "./Carousel.module.scss";
import cn from "clsx";
import CarouselCard from "../CustomCarouselCard/CarouselCard";

type BreakPoints = {
  [width: number]: {
    slidesPerView?: number;
    spaceBetween?: number;
    grid?: { rows?: number; fill: "row" | "column" };
  };
};

type BreakpointConfig = {
  slidesPerView?: number;
  spaceBetween?: number;
};

type CarouselFunctions = {
  nextSlide: () => void;
  prevSlide: () => void;
};

interface CarouselProps {
  children: React.ReactNode[];
  className?: string;
  slidesContainerClassName?: string;
  slideWrapperClassName?: string;
  isSmoothBehaviour?: boolean;
  id?: string;
  config?: {
    pagination?: {
      status?: boolean;
      clickable?: boolean;
      className?: string;
    };
    speed?: number;
    autoplay?: {
      status?: boolean;
      delay?: number;
    };
    breakpoints?: BreakPoints;
    navigation?: {
      status?: boolean;
      nextEl?: string;
      prevEl?: string;
    };
    slidesPerView?: number;
    spaceBetween?: number;
    allowTouchMove?: boolean | true;
    [key: string]: any;
  };
  mobileCardWidth?: number;
  isProgramListPage?: boolean;
}

const findBreakpoint = (
  screenWidth: number,
  breakpoints: BreakPoints,
): BreakpointConfig | number => {
  let currentSelectedPoint: BreakpointConfig | number = -1;

  for (const key in breakpoints) {
    const width = +key;

    if (width <= screenWidth && width > currentSelectedPoint) {
      currentSelectedPoint = width;
    }
  }

  if (currentSelectedPoint !== -1) return breakpoints[currentSelectedPoint];
  return currentSelectedPoint;
};

const Carousel = forwardRef<CarouselFunctions, CarouselProps>(
  (
    {
      children,
      className,
      config,
      slideWrapperClassName,
      slidesContainerClassName,
      id,
      isSmoothBehaviour,
      mobileCardWidth,
      isProgramListPage,
    },
    ref,
  ) => {
    const [cardWidth, setCardWidth] = useState<number>(0);
    const [autoplay, setAutoplay] = useState<boolean>(false);
    const [bulletPoints, setBulletPoints] = useState<number>(0);
    const [marginRight, setMarginRight] = useState<number>(0);
    const [totalCards, setTotalCards] = useState<number>(0);
    const [gridRows, setGridRows] = useState<number>(1);
    const [itemsPerRow, setItemPerRow] = useState<number>(children?.length);
    const paginationStatus = config?.pagination?.status !== false;
    const paginationBulletClickable = config?.pagination?.clickable !== false;
    const autoPlayStatus = config?.autoplay?.status !== false;
    const autoPlayDelay = config?.autoplay?.delay || 5000;
    const sliderTransitionSpeed = config?.speed || 1000;
    const cardWidthRef = useRef<number>(0);
    const maximumTranslate = useRef<number>(0);
    const breakpoints = config?.breakpoints;
    let breakPointConfig = null;
    const containerRef = useRef<HTMLDivElement>(null);
    const [currentIndex, setCurrentIndex] = useState(0);

    const isDragging = useRef(false);
    const startX = useRef(0);
    const currentTranslate = useRef(0);
    const prevTranslate = useRef(0);
    const animationRef = useRef(0);
    const autoPlayTimer = useRef<NodeJS.Timeout | null>(null);
    const isScrollingVertically = useRef(false);
    const startY = useRef(0);

    const setSliderPosition = () => {
      if (containerRef.current) {
        containerRef.current.style.transform = `translateX(${currentTranslate.current}px)`;
      }
    };

    const animation = () => {
      setSliderPosition();
      animationRef.current = requestAnimationFrame(animation);
    };

    const countCardsToMove = (movedCards: number) => {
      // checking fractional part of movedCards
      const fractionalPart: number = parseFloat((movedCards % 1).toFixed(1)); // only positive fractional part e.g. 0.5
      const integralPart = movedCards | 0; // integral part of movedCards

      if (fractionalPart >= 0.2) {
        return integralPart + 1;
      } else if (fractionalPart <= -0.2) {
        return integralPart - 1;
      } else {
        return integralPart;
      }
    };

    const touchStart = (x: number, y?: number) => {
      isDragging.current = true;
      startX.current = x;
      startY.current = y || 0; // new
      isScrollingVertically.current = false; // reset on new touch

      if (autoPlayTimer.current) {
        clearInterval(autoPlayTimer.current);
        autoPlayTimer.current = null;
      }

      if (containerRef.current) {
        containerRef.current.style.transition = "none";
      }
    };

    const touchMove = (x: number, y?: number) => {
      if (!isDragging.current) return;

      // Detect if it's vertical scrolling
      const diffX = x - startX.current;
      const diffY = (y || 0) - (startY.current || 0);

      if (!isScrollingVertically.current) {
        if (Math.abs(diffY) > Math.abs(diffX)) {
          isScrollingVertically.current = true; // mark as vertical scroll
        }
      }

      if (isScrollingVertically.current) {
        return; // stop horizontal sliding if vertical scroll detected
      }

      currentTranslate.current = prevTranslate.current + diffX;

      if (containerRef.current) {
        containerRef.current.style.transition = "none";
        containerRef.current.style.transform = `translateX(${currentTranslate.current}px)`;
      }
    };

    const touchEnd = () => {
      isDragging.current = false;

      if (containerRef.current && !autoPlayTimer.current) {
        containerRef.current.style.transition = `transform ${200 / 1000}s`;
      }

      const movedBy = currentTranslate.current - prevTranslate.current;

      if (!containerRef.current) return;

      // Calculate how many full card widths the swipe covers
      const movedCards = countCardsToMove(movedBy / cardWidthRef.current);

      let nextIndex = Math.floor(currentIndex - movedCards); // Subtract because dragging left = move forward
      nextIndex = Math.min(
        Math.max(nextIndex, 0),
        Math.max(bulletPoints - 1, 0),
      );
      if (nextIndex === currentIndex && autoPlayStatus) {
        setCurrentIndex(nextIndex);
        containerRef.current.style.transition = `transform ${sliderTransitionSpeed / 1000}s`;
        autoPlayTimer.current = setInterval(() => {
          nextSlide();
        }, autoPlayDelay);
      }
      nextIndex = Math.min(Math.max(nextIndex, 0), totalCards);
      goToSlide(nextIndex);
    };

    const handleWheel = (e) => {
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        e.preventDefault(); // block horizontal browser scroll
        e.stopPropagation(); // block horizontal browser scroll

        containerRef.current.style.transition = "none";
        currentTranslate.current -= 2 * e.deltaX;
        // Clamp within bounds
        currentTranslate.current = Math.max(
          Math.min(currentTranslate.current, 0),
          maximumTranslate.current,
        );
        containerRef.current.style.transform = `translateX(${currentTranslate.current}px)`;
      }
    };

    const goToSlide = (index: number) => {
      const container = containerRef.current;
      if (container) {
        const slideWidth = container.offsetWidth;
        currentTranslate.current =
          -index * ((cardWidthRef.current || slideWidth) + marginRight);
        prevTranslate.current = currentTranslate.current;
        setCurrentIndex(Math.ceil(index));
        setSliderPosition();

        // Trigger onSlideChange callback if provided
        if (typeof config?.onSlideChange === "function") {
          const isBeginning = index === 0;
          const isEnd = index >= totalCards;
          config?.onSlideChange({
            isBeginning,
            isEnd,
            index: Math.ceil(index),
          });
        }
      }
    };

    const nextSlide = useCallback(() => {
      const nextIndex = (currentIndex + 1) % bulletPoints;
      goToSlide(nextIndex > totalCards ? totalCards : nextIndex);
    }, [currentIndex, bulletPoints, totalCards]);

    const prevSlide = useCallback(() => {
      const prevIndex = (currentIndex - 1 + bulletPoints) % bulletPoints;
      goToSlide(prevIndex);
    }, [currentIndex, bulletPoints]);

    const startAutoplay = useCallback(() => {
      if (autoplay && autoPlayStatus && !autoPlayTimer.current) {
        autoPlayTimer.current = setInterval(() => {
          nextSlide();
        }, autoPlayDelay);
      }
    }, [autoplay, autoPlayStatus, nextSlide, autoPlayDelay]);

    const stopAutoplay = useCallback(() => {
      if (autoPlayTimer.current) {
        clearInterval(autoPlayTimer.current);
        autoPlayTimer.current = null;
      }
    }, []);

    useImperativeHandle(ref, () => ({
      nextSlide,
      prevSlide,
      startAutoplay,
      stopAutoplay,
    }));

    const arrangeCards = () => {
      if (breakpoints !== undefined && Object.keys(breakpoints)?.length > 0) {
        const result = findBreakpoint(window.innerWidth, breakpoints);
        breakPointConfig = typeof result === "object" ? result : -1;
        if (breakPointConfig !== -1) {
          const slidesPerView =
            breakPointConfig?.slidesPerView > children?.length
              ? children?.length
              : breakPointConfig?.slidesPerView;
          const spaceBetween =
            breakPointConfig?.spaceBetween || config?.spaceBetween || 1;
          const totalSpaceBetweenCards = Math.floor(
            spaceBetween * Math.floor(Math.ceil(slidesPerView) - 1),
          );
          const slideWidth = +(
            (containerRef.current.offsetWidth - totalSpaceBetweenCards) /
            slidesPerView
          ).toFixed(3);
          cardWidthRef.current = slideWidth;
          setCardWidth(slideWidth);
          setMarginRight(spaceBetween);
          maximumTranslate.current =
            -(slideWidth + spaceBetween) * (children?.length - slidesPerView);
        } else {
          const slidesPerView = config?.slidesPerView || 1;
          const spaceBetween = config?.spaceBetween || 1;
          const totalSpaceBetweenCards = Math.floor(
            spaceBetween * Math.floor(Math.ceil(slidesPerView) - 1),
          );
          const slideWidth = +(
            (containerRef.current.offsetWidth - totalSpaceBetweenCards) /
            slidesPerView
          ).toFixed(3);
          cardWidthRef.current = slideWidth;
          setCardWidth(slideWidth);
          setMarginRight(spaceBetween);
          maximumTranslate.current =
            -(slideWidth + spaceBetween) * (children?.length - slidesPerView);
        }
      } else {
        const slidesPerView = config?.slidesPerView || 1;
        const spaceBetween = config?.spaceBetween || 1;
        const totalSpaceBetweenCards = Math.floor(
          spaceBetween * Math.floor(Math.ceil(slidesPerView) - 1),
        );
        const slideWidth = +(
          (containerRef.current.offsetWidth - totalSpaceBetweenCards) /
          slidesPerView
        ).toFixed(3);
        cardWidthRef.current = slideWidth;
        setCardWidth(slideWidth);
        setMarginRight(spaceBetween);
        maximumTranslate.current =
          -(slideWidth + spaceBetween) * (children?.length - slidesPerView);
      }
    };

    useEffect(() => {
      if (autoplay && autoPlayStatus) {
        autoPlayTimer.current = setInterval(() => {
          nextSlide();
        }, autoPlayDelay);
      }

      return () => {
        if (autoPlayTimer.current) clearInterval(autoPlayTimer.current);
      };
    }, [nextSlide, autoPlayDelay, autoplay]);

    useLayoutEffect(() => {
      if (children && children?.length > 0) {
        const handleResize = () => arrangeCards();
        window.addEventListener("resize", handleResize);
        arrangeCards();
        return () => {
          window.removeEventListener("resize", handleResize);
        };
      }
    }, [children]);

    useEffect(() => {
      let rowSliderLen = children?.length;

      if (breakpoints !== undefined && Object.keys(breakpoints)?.length > 0) {
        const result = findBreakpoint(window.innerWidth, breakpoints);
        breakPointConfig = typeof result === "object" ? result : -1;
        if (breakPointConfig !== -1) {
          const slidesPerView =
            breakPointConfig?.slidesPerView > children?.length
              ? children?.length
              : breakPointConfig?.slidesPerView;
          if (config?.isGrid) {
            const gridRows = breakPointConfig?.grid?.rows || 1;
            rowSliderLen = Math.ceil(children?.length / Math.floor(gridRows));
            setGridRows(gridRows);
            setItemPerRow(rowSliderLen);
          }
          setAutoplay(rowSliderLen > slidesPerView);
          setBulletPoints(
            rowSliderLen <= slidesPerView
              ? 0
              : Math.ceil(rowSliderLen - slidesPerView + 1),
          );
          setTotalCards(
            rowSliderLen - slidesPerView < 0 ? 0 : rowSliderLen - slidesPerView,
          );
        } else {
          const slidesPerView = config?.slidesPerView || 1;
          if (config?.isGrid) {
            const gridRows = config?.grid?.rows || 1;
            rowSliderLen = Math.ceil(children?.length / Math.floor(gridRows));
            setGridRows(gridRows);
            setItemPerRow(rowSliderLen);
          }
          setAutoplay(rowSliderLen > slidesPerView);
          setBulletPoints(
            rowSliderLen <= slidesPerView
              ? 0
              : Math.ceil(rowSliderLen - slidesPerView + 1),
          );
          setTotalCards(
            rowSliderLen - slidesPerView < 0 ? 0 : rowSliderLen - slidesPerView,
          );
        }
      } else {
        const slidesPerView = config?.slidesPerView || 1;
        if (config?.isGrid) {
          const gridRows = config?.grid?.rows || 1;
          rowSliderLen = Math.ceil(children?.length / Math.floor(gridRows));
          setGridRows(gridRows);
          setItemPerRow(rowSliderLen);
        }
        setAutoplay(rowSliderLen > slidesPerView);
        setBulletPoints(
          rowSliderLen <= slidesPerView
            ? 0
            : Math.ceil(rowSliderLen - slidesPerView + 1),
        );
        setTotalCards(
          rowSliderLen - slidesPerView < 0 ? 0 : rowSliderLen - slidesPerView,
        );
      }
    }, [children, breakpoints, cardWidth]);

    useEffect(() => {
      if (config?.pageType === "program") {
        goToSlide(0);
      }
    }, [children, totalCards]);

    useEffect(() => {
      if (bulletPoints <= 0 && typeof config?.onSlideChange === "function") {
        config?.onSlideChange({ isBeginning: true, isEnd: true });
      } else if (typeof config?.onSlideChange === "function") {
        config?.onSlideChange({ isBeginning: true, isEnd: false });
      }
    }, [bulletPoints]);

    if (config?.freeMode) {
      return (
        <>
          <div ref={containerRef} style={{ width: "100%" }}></div>
          {!!cardWidth && (
            <div
              className={cn(className, styles.freeModeCarouselWrapper, "!p-0")}
            >
              <div className={styles.freeModeCarouselContainer}>
                {!config?.isGrid &&
                  children?.map((child, index) => (
                    <CarouselCard
                      cardWidth={cardWidthRef.current}
                      currentIndex={currentIndex}
                      child={child}
                      index={index}
                      key={index}
                      marginRight={marginRight}
                      listLen={children?.length || 0}
                      {...(id ? { id: `${id}-${index}` } : {})}
                    />
                  ))}
              </div>
            </div>
          )}
        </>
      );
    }

    return (
      <div className={cn(className, styles.carouselWrapper, "!p-0")}>
        <div
          className={cn(
            styles.carouselContainer,
            slidesContainerClassName,
            isSmoothBehaviour ? styles.smoothBehaviour : "",
          )}
          ref={containerRef}
          onTouchStart={(e) => {
            touchStart(e.touches[0].clientX, e.touches[0].clientY);
          }}
          onTouchMove={(e) => {
            touchMove(e.touches[0].clientX, e.touches[0].clientY);
          }}
          onTouchEnd={touchEnd}
          onTouchCancel={() => {
            touchEnd();
          }}
          onMouseDown={(e) => {
            touchStart(e.clientX, e.clientY);
          }}
          onMouseMove={(e) => {
            touchMove(e.clientX, e.clientY);
          }}
          onMouseUp={touchEnd}
          onMouseLeave={() => {
            if (isDragging.current) {
              touchEnd();
            }
          }}
          onWheel={(e) => {
            if (config?.freeMode) {
              handleWheel(e);
            }
          }}
          style={{
            transition: `transform ${sliderTransitionSpeed / 1000}s`,
          }}
        >
          {!config?.isGrid &&
            children?.map((child, index) => (
              <CarouselCard
                cardWidth={cardWidth}
                currentIndex={currentIndex}
                child={child}
                index={index}
                key={index}
                marginRight={marginRight}
                listLen={children?.length || 0}
                slideWrapperClassName={slideWrapperClassName}
                {...(id ? { id: `${id}-${index}` } : {})}
              />
            ))}

          {config?.isGrid && (
            <div className={styles.slideGrid}>
              {Array.from({ length: gridRows })?.map((_, rowIndex) => {
                const currentIndex = rowIndex * itemsPerRow;
                const rowItems = children?.slice(
                  currentIndex,
                  currentIndex + itemsPerRow,
                );
                return (
                  <div className={styles.row} key={`row-${rowIndex}`}>
                    {rowItems?.map((child, childIndex) => {
                      return (
                        <CarouselCard
                          cardWidth={cardWidth}
                          currentIndex={currentIndex}
                          child={child}
                          index={childIndex}
                          key={childIndex}
                          marginRight={marginRight}
                          listLen={rowItems?.length || 0}
                          id=""
                        />
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {config?.navigation?.status && (
          <div className={styles.controls}>
            <button onClick={prevSlide} className={config?.navigation?.prevEl}>
              ‹
            </button>
            <button onClick={nextSlide} className={config?.navigation?.nextEl}>
              ›
            </button>
          </div>
        )}

        {paginationStatus && children?.length > totalCards && (
          <div
            className={cn(
              styles.default_carousel_pagination,
              config?.pagination?.className,
            )}
          >
            {Array?.from({ length: bulletPoints })?.map((_, i) => (
              <span
                {...(id ? { id: `${id}-bullet-${i}` } : {})}
                key={i}
                className={`${cn(styles.bullet, "bullet_point")} ${
                  i === currentIndex
                    ? cn(styles.c_slide_active, "slide_active")
                    : ""
                }`}
                onClick={() => {
                  if (paginationBulletClickable) {
                    goToSlide(i > totalCards ? totalCards : i);
                  }
                }}
              />
            ))}
          </div>
        )}
      </div>
    );
  },
);

export default Carousel;
